import { ComputedRef, Ref, ref, watch } from "vue"
import {
  mdiAccountGroupOutline,
  mdiAccountLockOutline,
  mdiEyeOffOutline,
  mdiEyeOutline,
} from "@mdi/js"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { Group } from "suus-api"
import { ContentfulOption } from "components/UI/ContentfulSelect/index.vue"

const getContentfulOptions = () => {
  const { t$ } = useTranslate("components.topic_form")
  const OPEN = t$(".open")
  const INTERNAL = t$(".internal")
  const CONFIDENTIAL = t$(".confidential")
  const VISIBLE_FROM_OUTSIDE_GROUP = t$(".visible_from_outside_group")
  const NOT_VISIBLE_FROM_OUTSIDE_GROUP = t$(".not_visible_from_outside_group")
  const PARTICIPATE_FROM_OUTSIDE_GROUP = t$(".participate_from_outside_group")
  const PARTICIPATE_WITHIN_GROUP_ONLY = t$(".participate_within_group_only")

  return {
    [GroupAccess.OPEN]: {
      title: OPEN,
      type: GroupAccess.OPEN,
      contentItems: [
        {
          icon: mdiEyeOutline,
          text: VISIBLE_FROM_OUTSIDE_GROUP,
        },
        {
          icon: mdiAccountGroupOutline,
          text: PARTICIPATE_FROM_OUTSIDE_GROUP,
        },
      ],
    },
    [GroupAccess.INTERNAL]: {
      title: INTERNAL,
      type: GroupAccess.INTERNAL,
      contentItems: [
        {
          icon: mdiEyeOutline,
          text: VISIBLE_FROM_OUTSIDE_GROUP,
        },
        {
          icon: mdiAccountLockOutline,
          text: PARTICIPATE_WITHIN_GROUP_ONLY,
        },
      ],
    },
    [GroupAccess.CONFIDENTIAL]: {
      title: CONFIDENTIAL,
      type: GroupAccess.CONFIDENTIAL,
      contentItems: [
        {
          icon: mdiEyeOffOutline,
          text: NOT_VISIBLE_FROM_OUTSIDE_GROUP,
        },
        {
          icon: mdiAccountLockOutline,
          text: PARTICIPATE_WITHIN_GROUP_ONLY,
        },
      ],
    },
  }
}

const isInAccessOptions = (
  accessOptions: ContentfulOption[],
  accessValue: GroupAccess
) => accessOptions.some((option) => option.type === accessValue)

const _isOwnGroup = (group: Group, memberships: number[]) =>
  (!!group?.id && memberships.includes(group.id)) ?? false

const getDefaultValue = (
  isInternalGroup: boolean,
  accessOptions: ContentfulOption[]
) => {
  return isInternalGroup ? GroupAccess.INTERNAL : accessOptions[0].type
}

export function useAccessOptions(
  group: Ref<Group>,
  memberships: ComputedRef<number[]>,
  selectedAccess: ComputedRef<GroupAccess>,
  updateSelectedAccess: (access: GroupAccess) => void,
  initialAccessValue?: GroupAccess
) {
  const contentfulOptions = getContentfulOptions()

  const accessOptions: Ref<ContentfulOption[]> = ref([
    contentfulOptions[GroupAccess.OPEN],
  ])

  watch(
    group,
    (newGroup) => {
      if (!newGroup) return
      const isOwnGroup = _isOwnGroup(newGroup, memberships.value)
      const isClosedGroup = newGroup.closed ?? false
      const isInternalGroup = isOwnGroup && !isClosedGroup

      const enableOpen =
        !isClosedGroup || initialAccessValue == GroupAccess.OPEN
      const enableInternal =
        isInternalGroup || initialAccessValue == GroupAccess.INTERNAL
      const enableConfidential =
        isOwnGroup ||
        isClosedGroup ||
        initialAccessValue == GroupAccess.CONFIDENTIAL

      accessOptions.value = [
        enableOpen && contentfulOptions[GroupAccess.OPEN],
        enableInternal && contentfulOptions[GroupAccess.INTERNAL],
        enableConfidential && contentfulOptions[GroupAccess.CONFIDENTIAL],
      ].filter(Boolean)

      // Update
      const needsUpdate =
        !selectedAccess.value ||
        !isInAccessOptions(accessOptions.value, selectedAccess.value)
      if (needsUpdate)
        updateSelectedAccess(
          getDefaultValue(isInternalGroup, accessOptions.value)
        )
    },
    { immediate: true }
  )

  return accessOptions
}
